<template>
    <div class="w-full text-sm">
        <div v-if="isLoading" class="text-gray-500">Loading client feedback…</div>
        <div v-else-if="loadFailed" class="text-red-700">Could not load client feedback. Close and reopen to try again.</div>
        <template v-else>
        <div v-if="canManageFeedback" class="mb-3">
            <button
                v-if="!showPastForm"
                type="button"
                class="text-xs text-accent-primary hover:underline"
                @click="openPastForm"
            >+ Add past feedback</button>
            <form
                v-else
                class="flex flex-wrap items-end gap-3 border border-gray-200 rounded-md bg-gray-50 p-3"
                @submit.prevent="savePastFeedback"
            >
                <label class="flex flex-col gap-1 text-xs font-medium text-gray-600">
                    Campaign
                    <select v-model="pastForm.campaignId" required class="h-8 w-64 border border-gray-300 rounded px-1 text-sm bg-white">
                        <option value="" disabled>Select a campaign…</option>
                        <option v-for="campaign in campaignOptions" :key="campaign.id" :value="campaign.id">
                            {{ campaign.campaign_name }}{{ campaign.client ? ` (${campaign.client.client_name})` : '' }}
                        </option>
                    </select>
                </label>
                <label class="flex flex-col gap-1 text-xs font-medium text-gray-600">
                    Date given
                    <input v-model="pastForm.recordedOn" type="date" required :max="todayISO" class="h-8 border border-gray-300 rounded px-1 text-sm bg-white">
                </label>
                <label class="flex flex-col gap-1 text-xs font-medium text-gray-600">
                    Feedback
                    <select v-model="pastForm.feedback" required class="h-8 w-40 border border-gray-300 rounded px-1 text-sm bg-white">
                        <option value="" disabled>Select…</option>
                        <option v-for="option in FEEDBACK_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>
                </label>
                <div class="flex gap-2 pb-1 text-xs">
                    <button type="submit" class="px-3 py-1.5 rounded bg-dark-primary text-white hover:bg-accent-primary disabled:bg-gray-300" :disabled="savingPast">
                        {{ savingPast ? 'Saving…' : 'Save' }}
                    </button>
                    <button type="button" class="px-2 py-1.5 text-gray-600 hover:underline" @click="showPastForm = false">Cancel</button>
                </div>
            </form>
        </div>
        <div v-if="!clientGroups.length" class="text-gray-500">No client feedback or notes have been recorded for this company.</div>
        <div v-else class="flex flex-col gap-3">
            <section v-for="group in clientGroups" :key="group.key" class="border border-gray-200 rounded-md bg-white">
                <button
                    type="button"
                    class="w-full flex items-center gap-1.5 px-3 py-2 text-left font-semibold text-gray-800 hover:bg-gray-50"
                    :class="{ 'border-b border-gray-200': isOpen(group) }"
                    :aria-expanded="isOpen(group)"
                    @click="toggle(group)"
                >
                    <ChevronUpMiniSVG v-if="isOpen(group)" fill="black" iconClass="h-3.5 w-3.5" />
                    <ChevronDownMiniSVG v-else fill="black" iconClass="h-3.5 w-3.5" />
                    {{ group.clientName }}
                    <span v-if="group.isCurrent" class="ml-1 text-xs font-normal text-gray-500">(current client)</span>
                    <span class="ml-auto text-xs font-normal text-gray-500">
                        {{ group.feedback.length }} feedback · {{ group.notes.length }} {{ group.notes.length === 1 ? 'note' : 'notes' }}
                    </span>
                </button>
                <div v-show="isOpen(group)" class="px-3 py-2 flex flex-col gap-4">
                    <div v-for="part in parts(group)" :key="part.kind">
                        <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{{ part.title }}</h3>
                        <div v-if="!part.rows.length" class="text-xs text-gray-400 py-1">None recorded.</div>
                        <!-- Fixed columns, shared by both tables, so actions and long notes never shift the layout -->
                        <table v-else class="w-full table-fixed border-collapse">
                            <colgroup>
                                <col class="w-24"><col class="w-20"><col><col class="w-40"><col class="w-32"><col class="w-24">
                            </colgroup>
                            <tbody>
                                <tr
                                    v-for="row in part.rows"
                                    :key="row.entry.key"
                                    class="align-top"
                                    :class="{ 'border-t border-gray-200': row.startsDay }"
                                >
                                    <td class="py-1 pr-2 font-medium whitespace-nowrap">{{ row.startsDay ? row.date : '' }}</td>
                                    <td class="py-1 pr-2 text-gray-500 whitespace-nowrap">{{ isPastFeedback(row.entry) ? '' : formatTime(row.entry.created_at) }}</td>
                                    <td class="py-1 pr-2">
                                        <template v-if="part.kind === 'feedback'">
                                            <span class="capitalize">{{ row.entry.feedback ? row.entry.feedback.replaceAll('_', ' ') : 'Cleared' }}</span>
                                            <span v-if="isPastFeedback(row.entry)" class="ml-2 text-xs text-gray-400">entered {{ formatFeedbackDate(row.entry.updated_at) }}</span>
                                        </template>
                                        <div v-else-if="editingKey === row.entry.key" class="flex flex-col gap-1">
                                            <textarea
                                                v-model="draft"
                                                rows="3"
                                                class="w-full border border-gray-300 rounded p-1 text-sm"
                                                @keydown.esc.prevent="cancelEdit"
                                            ></textarea>
                                            <div class="flex gap-2 text-xs">
                                                <button type="button" class="text-accent-primary hover:underline disabled:text-gray-400" :disabled="busyKey === row.entry.key" @click="saveNote(row.entry)">Save</button>
                                                <button type="button" class="text-gray-500 hover:underline" @click="cancelEdit">Cancel</button>
                                            </div>
                                        </div>
                                        <span v-else class="whitespace-pre-wrap break-words">{{ row.entry.notes || '--' }}</span>
                                    </td>
                                    <td class="py-1 pr-2 text-gray-500 truncate" :title="row.entry.campaign?.campaign_name">{{ row.entry.campaign?.campaign_name ?? '--' }}</td>
                                    <td class="py-1 pr-2 text-gray-500 truncate">{{ userName(row.entry.created_by) }}</td>
                                    <td class="py-1 text-right text-xs whitespace-nowrap">
                                        <template v-if="part.kind === 'feedback'">
                                            <button
                                                v-if="canManageFeedback"
                                                type="button"
                                                class="text-red-700 hover:underline disabled:text-gray-400"
                                                :disabled="busyKey === row.entry.key"
                                                @click="deleteFeedback(row.entry)"
                                            >Delete</button>
                                        </template>
                                        <template v-else-if="editingKey !== row.entry.key">
                                            <button type="button" class="text-accent-primary hover:underline mr-2" @click="startEdit(row.entry)">Edit</button>
                                            <button
                                                type="button"
                                                class="text-red-700 hover:underline disabled:text-gray-400"
                                                :disabled="busyKey === row.entry.key"
                                                @click="deleteNote(row.entry)"
                                            >Delete</button>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
        </template>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { getCompanyByID, createPastCompanyClientFeedback, deleteCompanyClientFeedback, updateCompanyClientFeedbackNote, deleteCompanyClientFeedbackNote,
    clientFeedbackErrorMessage } from '@/services/Company';
import { useToast } from 'vue-toast-notification';
import { formatFeedbackDate } from '@/utils/clientFeedback';
import ChevronDownMiniSVG from '../svg-components/ChevronDownMiniSVG.vue';
import ChevronUpMiniSVG from '../svg-components/ChevronUpMiniSVG.vue';

// Every client feedback mark and note ever recorded for the company, grouped by client, with
// the feedback and the notes in their own sections, each by the day it was recorded. Loads the company itself: the modal can be opened
// from pages whose companies don't carry the history.
const props = defineProps({
    company: Object
})

const emit = defineEmits(['refetch-companies'])

const store = useStore()
const $toast = useToast()

// The same groups the backend's CanMarkClientFeedback allows; anyone else would be sent to the forbidden page
const canManageFeedback = computed(() => {
    const permissions = store.state.auth.userPermissions ?? {}
    return Boolean(permissions.isDev || permissions.isAccountManager || permissions.isManagement
        || permissions.isSuperuser || permissions.isAccountStaff)
})
const FEEDBACK_OPTIONS = [
    { value: 'approved', label: 'Approved' },
    { value: 'excluded', label: 'Excluded' },
    { value: 'hold', label: 'Hold' },
    { value: 'pass', label: 'Pass' },
    { value: 'research_further', label: 'Research Further' },
]

// Past feedback: given on an earlier day, in a campaign the company has been in
const showPastForm = ref(false)
const savingPast = ref(false)
const pastForm = reactive({ campaignId: '', recordedOn: '', feedback: '' })
const campaignOptions = computed(() => detail.value?.feedback_campaign_options ?? [])
// Today as YYYY-MM-DD in the user's time zone, the latest day a past entry can have
const todayISO = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10)

// The entry being saved or deleted, and the note being edited
const busyKey = ref(null)
const editingKey = ref(null)
const draft = ref('')

const detail = ref(null)
const isLoading = ref(true)
const loadFailed = ref(false)

// A campaign with no client is its own group, the same rule the backend uses for "current client"
function clientKey(campaign) {
    if (!campaign) return 'none'
    return campaign.client ? `client-${campaign.client.id}` : `campaign-${campaign.id}`
}

function clientName(campaign) {
    if (!campaign) return 'No campaign'
    return campaign.client ? campaign.client.client_name : `No client (${campaign.campaign_name})`
}

// Newest day first; within a day, in the order recorded. startsDay marks the first row of each day.
function dayRows(entries) {
    const days = new Map()
    for (const entry of entries) {
        const date = formatFeedbackDate(entry.created_at)
        if (!days.has(date)) days.set(date, [])
        days.get(date).push(entry)
    }
    return [...days.entries()].reverse().flatMap(([date, dayEntries]) =>
        dayEntries.map((entry, index) => ({ entry, date, startsDay: index === 0 })))
}

// The current client first, then the others by their most recent entry
const clientGroups = computed(() => {
    if (!detail.value) return []
    const entries = [
        ...(detail.value.client_feedback_new ?? []).map((row) => ({ ...row, kind: 'feedback', key: `feedback-${row.id}` })),
        ...(detail.value.client_feedback_notes_new ?? []).map((row) => ({ ...row, kind: 'note', key: `note-${row.id}` })),
    ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))

    const groups = new Map()
    for (const entry of entries) {
        const key = clientKey(entry.campaign)
        if (!groups.has(key)) {
            groups.set(key, { key, clientName: clientName(entry.campaign), latest: entry.created_at, feedback: [], notes: [] })
        }
        const group = groups.get(key)
        group.latest = entry.created_at
        group[entry.kind === 'feedback' ? 'feedback' : 'notes'].push(entry)
    }

    const currentKey = clientKey(detail.value.campaign)
    return [...groups.values()]
        .map((group) => ({ ...group, isCurrent: group.key === currentKey }))
        .sort((a, b) => (b.isCurrent - a.isCurrent) || (new Date(b.latest) - new Date(a.latest)))
})

function parts(group) {
    return [
        { kind: 'feedback', title: 'Feedback', rows: dayRows(group.feedback) },
        { kind: 'note', title: 'Notes', rows: dayRows(group.notes) },
    ]
}

// The current client's section starts open and the others closed; a click overrides either
const toggled = reactive({})

function isOpen(group) {
    return group.key in toggled ? toggled[group.key] : group.isCurrent
}

function toggle(group) {
    toggled[group.key] = !isOpen(group)
}

function formatTime(dateString) {
    return new Date(dateString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function userName(user) {
    if (!user) return '--'
    return `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || '--'
}

async function deleteFeedback(entry) {
    const value = entry.feedback ? entry.feedback.replaceAll('_', ' ') : 'cleared'
    if (!confirm(`Delete the "${value}" feedback from ${formatFeedbackDate(entry.created_at)}?`)) return
    await changeEntry(entry, () => deleteCompanyClientFeedback(entry.id, store.state.auth.token), 204)
}

function startEdit(entry) {
    editingKey.value = entry.key
    draft.value = entry.notes ?? ''
}

function cancelEdit() {
    editingKey.value = null
    draft.value = ''
}

async function saveNote(entry) {
    if (await changeEntry(entry, () => updateCompanyClientFeedbackNote(entry.id, draft.value, store.state.auth.token), 200)) {
        cancelEdit()
    }
}

async function deleteNote(entry) {
    if (!confirm(`Delete this note from ${formatFeedbackDate(entry.created_at)}?`)) return
    await changeEntry(entry, () => deleteCompanyClientFeedbackNote(entry.id, store.state.auth.token), 204)
}

// Runs a save or delete, then reloads the history. The company's current feedback and notes may
// have changed, so the page behind the modal reloads too.
async function changeEntry(entry, request, expectedStatus) {
    busyKey.value = entry.key
    const result = await request()
    busyKey.value = null
    if (!result || result.status !== expectedStatus) {
        $toast.open({ type: 'error', duration: 10000, message: clientFeedbackErrorMessage(result) })
        return false
    }
    await loadHistory()
    emit('refetch-companies')
    return true
}

function openPastForm() {
    Object.assign(pastForm, { campaignId: '', recordedOn: '', feedback: '' })
    showPastForm.value = true
}

async function savePastFeedback() {
    savingPast.value = true
    const result = await createPastCompanyClientFeedback(
        props.company.id, pastForm.campaignId, pastForm.feedback, pastForm.recordedOn, store.state.auth.token)
    savingPast.value = false
    if (!result || result.status !== 201) {
        $toast.open({ type: 'error', duration: 10000, message: clientFeedbackErrorMessage(result) })
        return
    }
    showPastForm.value = false
    await loadHistory()
    emit('refetch-companies')
}

// Backdated feedback was entered on a later day than the one it is dated to
function isPastFeedback(entry) {
    return formatFeedbackDate(entry.created_at) !== formatFeedbackDate(entry.updated_at)
}

async function loadHistory() {
    const result = await getCompanyByID({ token: store.state.auth.token, companyId: props.company.id })
    isLoading.value = false
    if (result && result.response && result.response.status === 200) {
        detail.value = result.response.data
    } else {
        loadFailed.value = true
    }
}

loadHistory()
</script>
